import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';

import { productsCartState } from '../../../../../../../store/batchTicket/atom';
import {
  AssetDefaultOrSelectedTypeMap,
  SubmitUpdateProps,
} from '../../../../../../../store/product/types';

import { CoesFormData, getValidationCreateSchema } from './constants';

export const useCoesAssetController = (
  offer: AssetDefaultOrSelectedTypeMap['coes'],
  submitUpdate?: (data: SubmitUpdateProps) => Promise<boolean> | boolean
) => {
  const [isFocused, setIsFocused] = useState(false);
  const [prevValue, setPrevValue] = useState(0);

  const coesOffersSelected = useRecoilValue(productsCartState).coes;

  const valueAppliedBasket = coesOffersSelected.items.findIndex(
    (item) => item.productId === offer.productId
  );

  const [valueApplied, setValueApplied] = useState(
    coesOffersSelected?.items[valueAppliedBasket]?.orderValue ||
      offer.orderValue ||
      offer.minimumApplicationNormalized
  );

  const valueAppliedFormatted = Utils.Mask.Money(valueApplied);
  const minValueFormatted = Utils.Mask.Money(offer.minimumApplicationNormalized);

  const tableForm = useForm<CoesFormData>({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: {
      createAppliedAmount: valueAppliedFormatted,
    },
    resolver: zodResolver(
      getValidationCreateSchema(
        offer.minimumApplicationNormalized,
        offer.maximumApplicationNormalized
      )
    ),
  });

  const isAmountWithinRange = (amount: number) => {
    return (
      offer.minimumApplicationNormalized &&
      prevValue !== amount &&
      amount >= offer.minimumApplicationNormalized &&
      amount <= offer.maximumApplicationNormalized
    );
  };

  const updateAppliedAmount = async (amount: number) => {
    const updateValues = (newAmount: number) => {
      const newFormattedAmount = Utils.Mask.Money(newAmount);
      tableForm.setValue('createAppliedAmount', newFormattedAmount);
      setValueApplied(newAmount);
      setPrevValue(newAmount);
    };

    if (submitUpdate) {
      const response = await submitUpdate({ amount });

      if (response) {
        updateValues(amount);
      } else {
        updateValues(prevValue);
      }
    } else {
      updateValues(amount);
    }

    const formattedAmount = Utils.Mask.Money(amount);
    tableForm.setValue('createAppliedAmount', formattedAmount);
    setValueApplied(amount);
    setPrevValue(amount);
  };

  const processInputAmount = (amount: number) => {
    if (isAmountWithinRange(amount)) {
      updateAppliedAmount(amount);
    }
  };

  const handleInputBlur = () => {
    const currentValue = tableForm.getValues('createAppliedAmount');
    const currentValueUnformatted = Utils.Unmask.Money(currentValue);

    if (currentValue !== minValueFormatted) {
      processInputAmount(currentValueUnformatted);
    }

    if (isFocused) {
      setIsFocused(false);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setPrevValue(offer.minimumApplicationNormalized || 0);

    if (tableForm.formState.errors?.createAppliedAmount) {
      tableForm.resetField('createAppliedAmount');
    }
  };

  const handleInputClear = () => {
    setIsFocused(false);
    tableForm.resetField('createAppliedAmount');
    tableForm.clearErrors('createAppliedAmount');
  };

  const handleSubmit = async ({ createAppliedAmount }: CoesFormData) => {
    const unformattedAmount = Utils.Unmask.Money(createAppliedAmount);

    const updateFormValues = (amount: number) => {
      tableForm.setValue('createAppliedAmount', Utils.Mask.Money(amount));
      setValueApplied(amount);
    };

    if (submitUpdate) {
      const response = await submitUpdate({ amount: unformattedAmount });

      if (response) {
        updateFormValues(unformattedAmount);
      } else {
        updateFormValues(prevValue);
      }
    } else {
      updateFormValues(unformattedAmount);
    }

    setIsFocused(false);
  };

  const helpTextWhenIsFocused = `Mínimo: ${Utils.Mask.Money(offer.minimumApplicationNormalized)}`;

  const productsDetailText = 'Veja mais detalhes\nsobre este produto';

  const createAppliedAmountValue = tableForm.getValues('createAppliedAmount');

  return {
    handleInputFocus,
    handleInputClear,
    isFocused,
    handleInputBlur,
    handleSubmit: tableForm.handleSubmit(handleSubmit),
    control: tableForm.control as unknown as Control,
    helpText: helpTextWhenIsFocused,
    hasError: tableForm.formState.errors?.createAppliedAmount,
    createAppliedAmountValue,
    currentValue: valueApplied,
    currentValueFormatted: valueAppliedFormatted,
    productsDetailText,
  };
};

export type CoesController = ReturnType<typeof useCoesAssetController>;
